import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';

/** Bundled program art: PNG, then SVG in theme/programs, then default Liga logo. */
export function bundledProgramImageUrl(shortName: string | undefined): string {
  if (!shortName || shortName.trim() === '') {
    return require('@/theme/liga-logo-white.png');
  }
  const sn = shortName.trim();
  try {
    return require(`@/theme/programs/${sn}.png`);
  } catch {
    try {
      return require(`@/theme/programs/${sn}.svg`);
    } catch {
      return require('@/theme/liga-logo-white.png');
    }
  }
}

export default class LogoService {
  private static LOGO_DIRECTORY = 'logo';
  private static logoCache: Map<string, string> = new Map();

  private getApiBase(): string {
    const v = process.env.VUE_APP_API_URL || process.env.VUE_APP_API_EXT_URL || '';
    return v && !v.endsWith('/') ? `${v}/` : v;
  }

  /**
   * В dev при ionic serve (порт ≠ API) — пути /api/... и прокси vue.config.js.
   * В prod всегда полный URL из env (отдельный домен API не ломаем).
   */
  private apiPrefixForWebFetch(): string {
    if (Capacitor.isNativePlatform()) {
      return this.getApiBase();
    }
    if (typeof window === 'undefined') {
      return this.getApiBase();
    }
    if (process.env.NODE_ENV !== 'development') {
      return this.getApiBase();
    }
    const raw = (process.env.VUE_APP_API_URL || process.env.VUE_APP_API_EXT_URL || '').trim();
    if (!raw) {
      return '/';
    }
    try {
      const normalized = raw.endsWith('/') ? raw : `${raw}/`;
      if (new URL(normalized).origin === window.location.origin) {
        return normalized;
      }
    } catch {
      return this.getApiBase();
    }
    return '/';
  }

  private joinApiPath(pathWithoutLeadingSlash: string): string {
    const prefix = this.apiPrefixForWebFetch();
    if (prefix === '/') {
      return `/${pathWithoutLeadingSlash}`;
    }
    return prefix.endsWith('/') ? `${prefix}${pathWithoutLeadingSlash}` : `${prefix}/${pathWithoutLeadingSlash}`;
  }

  private getServerLogoUrl(shortName: string): string {
    return this.joinApiPath(`api/program-logos/${encodeURIComponent(shortName)}`);
  }

  private getLogoExistsUrl(shortName: string): string {
    return this.joinApiPath(`api/program-logos/${encodeURIComponent(shortName)}/exists`);
  }

  private getFallback(shortName: string): string {
    return bundledProgramImageUrl(shortName);
  }

  /** Drop cached URL so the next getProgramLogo re-checks the server (e.g. after img @error). */
  forgetProgramLogo(shortName: string): void {
    const prev = LogoService.logoCache.get(shortName);
    if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
    LogoService.logoCache.delete(shortName);
  }

  /**
   * Get program logo URL: server logo (api/program-logos/:shortName) or fallback from theme.
   */
  async getProgramLogo(shortName: string | undefined, forceRefresh = false): Promise<string> {
    if (!shortName || shortName.trim() === '') {
      return bundledProgramImageUrl(undefined);
    }

    if (forceRefresh) {
      const old = LogoService.logoCache.get(shortName);
      if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
      LogoService.logoCache.delete(shortName);
      if (Capacitor.isNativePlatform()) {
        try {
          await Filesystem.deleteFile({
            path: `${LogoService.LOGO_DIRECTORY}/${shortName}.png`,
            directory: Directory.Data,
          });
        } catch {
          /* ignore */
        }
      }
    }

    if (!forceRefresh && LogoService.logoCache.has(shortName)) {
      const cached = LogoService.logoCache.get(shortName);
      if (cached) return cached;
    }

    const existsUrl = this.getLogoExistsUrl(shortName);
    const serverUrl = this.getServerLogoUrl(shortName);

    try {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      let status: number;
      if (Capacitor.isNativePlatform()) {
        const response = await CapacitorHttp.request({
          method: 'GET',
          url: existsUrl,
          headers,
        });
        const s = response.status;
        status = typeof s === 'string' ? parseInt(s, 10) : s;
      } else {
        const res = await fetch(existsUrl, { method: 'GET', headers });
        status = res.status;
      }

      // Только 204: ответ 200 без тела (например HTML от прокси) не считаем «логотип есть».
      if (status === 204) {
        LogoService.logoCache.set(shortName, serverUrl);
        return serverUrl;
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`Logo exists check skipped for ${shortName} (network/CORS); using bundled image.`, e);
      }
    }
    const fallback = this.getFallback(shortName);
    LogoService.logoCache.set(shortName, fallback);
    return fallback;
  }

  /**
   * Return server logo URL without checking existence (e.g. for img src with @error fallback).
   */
  getProgramLogoServerUrl(shortName: string | undefined): string {
    if (!shortName || shortName.trim() === '') return bundledProgramImageUrl(undefined);
    return this.getServerLogoUrl(shortName);
  }

  /**
   * Download all program logos (mobile: pre-populate memory cache).
   */
  async downloadAllProgramLogos(programs: Array<{ shortName: string }>): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await Promise.allSettled(programs.map((p) => this.getProgramLogo(p.shortName, false)));
  }

  /**
   * Refresh a specific logo (clear cache and re-fetch).
   */
  async refreshLogo(shortName: string): Promise<string> {
    return this.getProgramLogo(shortName, true);
  }

  async refreshAllLogos(programs: Array<{ shortName: string }>): Promise<void> {
    await Promise.allSettled(programs.map((p) => this.refreshLogo(p.shortName)));
  }

  async clearCache(): Promise<void> {
    LogoService.logoCache.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    LogoService.logoCache.clear();
    if (Capacitor.isNativePlatform()) {
      try {
        const { files } = await Filesystem.readdir({
          path: LogoService.LOGO_DIRECTORY,
          directory: Directory.Data,
        });
        for (const file of files) {
          await Filesystem.deleteFile({
            path: `${LogoService.LOGO_DIRECTORY}/${file}`,
            directory: Directory.Data,
          }).catch(() => {});
        }
      } catch {
        /* ignore */
      }
    }
  }
}
